// src/HomePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from './assets/logo.png';       // شعار المكتبة
import footer1 from './assets/footer1.png'; // الصورة الأولى للفوتر
import footer2 from './assets/footer2.png'; // الصورة الثانية للفوتر

const globalCSS = `
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');
* { margin:0; padding:0; box-sizing:border-box }
:root {
  --glow-color: #FFD700;
  --glow-color-2: #FFF176;
}

/* نخلي الارتفاع ممتد ونسمح بتمرير عمودي فقط */
html, body, #root { height: 100%; }
body {
  font-family:'Cairo',sans-serif;
  background:linear-gradient(to bottom,#0b0b2b,#1b2735 70%,#090a0f);
  overflow-x:hidden;
  overflow-y:auto;
}

/* خلفية نجوم ثابتة (ما تسبب تمدد للصفحة) */
.stars, .stars::after {
  position:fixed; top:0; left:0; width:1px; height:1px; background:#fff; pointer-events:none;
}
.stars {
  box-shadow:
    2vw 5vh 2px #fff,10vw 8vh 2px #fff,15vw 15vh 1px #fff,22vw 22vh 1px #fff,
    28vw 12vh 2px #fff,32vw 32vh 1px #fff,38vw 18vh 2px #fff,42vw 35vh 1px #fff;
  animation:twinkle 8s linear infinite;
}
.stars::after {
  content:''; box-shadow:
    8vw 12vh 2px #fff,16vw 18vh 1px #fff,24vw 25vh 2px #fff,33vw 15vh 1px #fff;
  animation:twinkle 6s linear infinite reverse;
}
@keyframes twinkle {0%,100%{opacity:.8}50%{opacity:.4}}

/* شُهُب ثابتة */
.shooting-star {
  position:fixed; width:100px; height:2px; pointer-events:none;
  background:linear-gradient(90deg,#fff,transparent);
  animation:shoot 3s ease-in-out infinite;
  will-change: transform, opacity;
}
.shooting-star:nth-child(1){top:20%;left:-120px;animation-delay:0s}
.shooting-star:nth-child(2){top:40%;left:-120px;animation-delay:1s}
.shooting-star:nth-child(3){top:60%;left:-120px;animation-delay:2s}
@keyframes shoot {
  0% {transform:translateX(0) rotate(25deg);opacity:1}
  100% {transform:translateX(120vw) rotate(25deg);opacity:0}
}

/* إطار متوهّج ثابت */
.glow-ring {
  display:inline-block;
  border:6px solid var(--glow-color);
  border-radius:9999px;
  box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.45);
  animation: ringPulse 2s ease-in-out infinite, ringHue 4s ease-in-out infinite;
}
@keyframes ringPulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(255, 215, 0, 0.45); }
  50%     { box-shadow: 0 0 0 10px rgba(255, 215, 0, 0.15); }
}
@keyframes ringHue {
  0%,100% { border-color: var(--glow-color); }
  50%     { border-color: var(--glow-color-2); }
}

/* Footer ثابت ومتجاوب */
.footer {
  position: fixed;
  bottom: 0; left: 0;
  width: 100%;
  padding: 0.5rem 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 50;
  gap: 0.5rem;
  background: transparent;
}
.footer-item img {
  width: clamp(36px, 12vw, 88px);
  height: auto;
  object-fit: contain;
  display: block;
}
@media (max-width: 480px) {
  .footer-item img { width: clamp(28px, 16vw, 60px); }
}
`;

export default function HomePage() {
  const [loading, setLoading]         = useState(true);
  const [ready, setReady]             = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const navigate                      = useNavigate();

  // شاشة التحميل 3 ثواني
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 3000);
    return () => clearTimeout(t);
  }, []);

  // انتظار 0.5 ثانية بعد التحميل
  useEffect(() => {
    if (!loading) {
      const t = setTimeout(() => setReady(true), 500);
      return () => clearTimeout(t);
    }
  }, [loading]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const documentId = params.get('documentId');
    if (documentId) {
      navigate(`/verify-document?id=${encodeURIComponent(documentId)}`, { replace: true });
    }
  }, [navigate]);

  const handleNext = () => {
    setShowOptions(true); // بدون تمرير تلقائي
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: globalCSS }} />

      {/* خلفية */}
      <div className="stars" />
      <div className="shooting-star" />
      <div className="shooting-star" />
      <div className="shooting-star" />

      {/* المحتوى */}
      <div
        className="relative z-10 w-full"
        dir="rtl"
      >
        {/* نضيف حشوة سفلية تكفي الفوتر + حشوة علوية صغيرة للموبايل */}
        <div className="min-h-screen flex items-center justify-center px-4 pt-4 pb-24">
          <AnimatePresence>
            {loading && (
              <motion.div
                key="loader"
                className="flex flex-col items-center space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="flex" style={{ gap: '0.5rem' }}>
                  {['#000', '#FFD700', '#FFF176'].map((c, i) => (
                    <motion.div
                      key={i}
                      className="w-4 h-4 rounded-full"
                      style={{ background: c }}
                      animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </div>
                <p className="text-base sm:text-lg font-medium text-gray-300">جاري التحميل…</p>
              </motion.div>
            )}

            {ready && !loading && (
              <motion.div
                key="home"
                className="w-full max-w-sm sm:max-w-md md:max-w-3xl mx-auto text-center space-y-6 sm:space-y-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* شعار متجاوب */}
                <div className="glow-ring" style={{ padding: 10 }}>
                  <motion.img
                    src={logo}
                    alt="شعار المكتبة الشبابية المجانية"
                    className="rounded-full shadow-xl w-28 h-28 sm:w-36 sm:h-36 md:w-40 md:h-40"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                {/* العناوين والوصف */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
                  المكتبة الشبابية المجانية
                </h1>
                <div className="font-light text-[#FFD700] text-lg sm:text-xl">
                  FREE YOUTH LIBRARY
                </div>
                <div className="text-gray-300 text-sm sm:text-base">
                  أهلاً بكم في المكتبة الشبابية المجانية — استكشف الكتب واستعِرها بسهولة!
                </div>

                {/* زر “التالي” */}
                {!showOptions && (
                  <motion.button
                    onClick={handleNext}
                    className="px-8 sm:px-10 py-3 sm:py-4 bg-blue-600 text-white text-lg sm:text-xl font-semibold rounded-lg shadow-lg"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    whileHover={{ scale: 1.08, opacity: 0.9, transition: { duration: 0.2 } }}
                    whileTap={{ scale: 0.95, backgroundColor: '#047857', transition: { duration: 0.1 } }}
                  >
                    التالي
                  </motion.button>
                )}

                {/* بطاقتا الدخول */}
                <AnimatePresence>
                  {showOptions && (
                    <motion.div
                      className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      {/* أدمن */}
                      <motion.div
                        className="p-5 sm:p-6 bg-blue-50/70 rounded-xl shadow-lg backdrop-blur-sm flex flex-col"
                        initial={{ scale: 0.98, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.05 }}
                      >
                        <h3 className="text-lg sm:text-xl font-bold mb-2 text-blue-900">دخول الأدمن</h3>
                        <p className="text-gray-800 mb-4 text-sm sm:text-base">إدارة المكتبة بالكامل.</p>
                        <motion.button
                          onClick={() => navigate('/admin')}
                          className="mt-auto px-4 py-2 bg-blue-600 text-white rounded-lg shadow text-sm sm:text-base"
                          whileHover={{ scale: 1.03, backgroundColor: '#000', transition: { duration: 0.2 } }}
                          whileTap={{ scale: 0.97, backgroundColor: '#047857', transition: { duration: 0.1 } }}
                        >
                          اذهب →
                        </motion.button>
                      </motion.div>

                      {/* كاشير */}
                      <motion.div
                        className="p-5 sm:p-6 bg-green-50/70 rounded-xl shadow-lg backdrop-blur-sm flex flex-col"
                        initial={{ scale: 0.98, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        <h3 className="text-lg sm:text-xl font-bold mb-2 text-green-900">دخول الكاشير</h3>
                        <p className="text-gray-800 mb-4 text-sm sm:text-base">واجهة سريعة للإعارة والإرجاع.</p>
                        <motion.button
                          onClick={() => navigate('/cashier')}
                          className="mt-auto px-4 py-2 bg-green-600 text-white rounded-lg shadow text-sm sm:text-base"
                          whileHover={{ scale: 1.03, backgroundColor: '#000', transition: { duration: 0.2 } }}
                          whileTap={{ scale: 0.97, backgroundColor: '#1E3A8A', transition: { duration: 0.1 } }}
                        >
                          اذهب →
                        </motion.button>
                      </motion.div>
                      <motion.div
                        className="p-5 sm:p-6 bg-yellow-50/80 rounded-xl shadow-lg backdrop-blur-sm flex flex-col"
                        initial={{ scale: 0.98, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.15 }}
                      >
                        <h3 className="text-lg sm:text-xl font-bold mb-2 text-yellow-900">التحقق من الوثيقة</h3>
                        <p className="text-gray-800 mb-4 text-sm sm:text-base">ابحث عن رقم الوثيقة واعرض ملف PDF مباشرة.</p>
                        <motion.button
                          onClick={() => navigate('/verify-document')}
                          className="mt-auto px-4 py-2 bg-yellow-500 text-slate-950 rounded-lg shadow text-sm sm:text-base font-bold"
                          whileHover={{ scale: 1.03, backgroundColor: '#facc15', transition: { duration: 0.2 } }}
                          whileTap={{ scale: 0.97, backgroundColor: '#eab308', transition: { duration: 0.1 } }}
                        >
                          اذهب →
                        </motion.button>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* فوتر ثابت ومتجاوب */}
      <div className="footer" dir="rtl">
        <div className="footer-item">
          <img src={footer1} alt="footer1" loading="lazy" decoding="async" />
        </div>
        <div className="footer-item">
          <img src={footer2} alt="footer2" loading="lazy" decoding="async" />
        </div>
      </div>
    </>
  );
}
